import React, { useEffect, useState } from "react";
import { eventObject, HeaderObject } from "../../../types/types";
import { EventTableRow } from "./EventTableRow";
import { EventTableHeader } from "./EventTableHeader";
import { Pagination } from "../../../components/utils/Pagination";
import { SearchBar } from "../../../components/utils/SearchBar";
import {
  searchArrayByValue,
  getPageList,
  toggleKey,
  sortByKey,
  move,
  insert,
  mapFromKeysEventObject,
  getUniqueKeysForTable,
} from "../../../utils/functions";
import { ErrorFetching } from "../../../components/modals/ErrorFetching";
import { useSelector } from "react-redux";
import { DropResult } from "react-beautiful-dnd";
import * as erState from "../../../reducers/errorReducer";
import { useNotify } from "../../../hooks/useNotify";
import { Loading } from "../../../components/utils/Loading";

interface EventTableProps {
  list: eventObject[];
  openEvent: (eventId: string, subEventId?: string) => void;
  loading: boolean;
}

export const EventTable: React.FC<EventTableProps> = ({
  list,
  openEvent,
  loading,
}) => {
  const { message } = useSelector(erState.getErrorMessage);
  const errorMsg = useNotify(message);
  const headerRow: string[] = [
    "eventName",
    "eventPlace",
    "startDate",
    "endDate",
    "eventState",
    "registered",
    "que",
  ];
  const filterDivToggleId = "LIST_EVENTS_TABLE_FILTER";
  const tableSettingsDivId = "LIST_EVENTS_SETTINGS";
  const exportSettingsDivId = "LIST_EVENTS_EXPORT_SETTINGS";
  const pageLimit = 7;
  const customKeys = ["que", "registered", "show", "cancelled"];
  const initialState = {
    currentPage: 1,
    totalPages: Math.ceil(list.length / pageLimit),
    pageLimit: pageLimit,
    totalRecords: list.length,
    pageNeighbours: 0,
    filteredList: list,
    currentList: list.slice(0, pageLimit),
    filterKeys: [],
    searchWord: "",
    sortKey: headerRow[0],
    ascending: false,
    possibleSearchKeys: list.length ? getUniqueKeysForTable(list) : [],
    headers: {
      droppableColumnsSelected: headerRow,
      droppableColumnsAll: list.length
        ? customKeys
            .concat(getUniqueKeysForTable(list))
            .filter((item) => !headerRow.includes(item))
        : [],
    },
    export: {
      exportAll: {
        data: mapFromKeysEventObject(
          list,
          getUniqueKeysForTable(list),
          customKeys
        ),
        filename: "events_all",
        label: "export all",
      },
      exportView: {
        data: mapFromKeysEventObject(list, headerRow, customKeys),
        filename: "events_view",
        label: "export view",
      },
    },
  };

  const [state, setState] = useState(initialState);

  const onDragEnd = (dragObject: DropResult) => {
    if (dragObject.destination) {
      if (
        dragObject.source.droppableId === dragObject.destination.droppableId
      ) {
        const updated = move(
          state.headers[dragObject.destination.droppableId],
          dragObject.source.index,
          dragObject.destination.index
        );
        const updatedHeaders: HeaderObject = {
          ...state.headers,
          [dragObject.destination.droppableId]: updated,
        };
        const newExportedView =
          dragObject.destination.droppableId === "droppableColumnsSelected"
            ? mapFromKeysEventObject(state.filteredList, updated, customKeys)
            : state.export.exportView.data;
        setState({
          ...state,
          headers: updatedHeaders,
          export: {
            ...state.export,
            exportView: {
              ...state.export.exportView,
              data: newExportedView,
            },
          },
        });
      } else {
        const updatedSource: string[] = state.headers[
          dragObject.source.droppableId
        ].filter((header) => header !== dragObject.draggableId);
        const updatedDestination: string[] = insert(
          state.headers[dragObject.destination.droppableId],
          dragObject.destination.index,
          dragObject.draggableId
        );
        const updatedHeaders: HeaderObject = ({
          [dragObject.source.droppableId]: updatedSource,
          [dragObject.destination.droppableId]: updatedDestination,
        } as unknown) as HeaderObject;
        const newExportedView = mapFromKeysEventObject(
          state.filteredList,
          updatedHeaders.droppableColumnsSelected,
          customKeys
        );
        setState({
          ...state,
          headers: updatedHeaders,
          export: {
            ...state.export,
            exportView: {
              ...state.export.exportView,
              data: newExportedView,
            },
          },
        });
      }
    }
  };

  const gotoPage = (page: number) => {
    const currentList = getPageList(page, state.pageLimit, state.filteredList);
    const updated = {
      ...state,
      currentPage: page,
      currentList,
    };
    setState(updated);
  };

  const toggleFilter = (key: string) => {
    const updated = {
      ...state,
      filterKeys: toggleKey(key, state.filterKeys),
    };
    setState(updated);
  };

  const sort = (key: string) => {
    const sorted = sortByKey(key, state.ascending, state.filteredList);
    const newExportedView = mapFromKeysEventObject(
      sorted,
      state.headers.droppableColumnsSelected,
      customKeys
    );
    const updated = {
      ...state,
      ascending: !state.ascending,
      sortKey: key,
      filteredList: sorted,
      currentList: sorted.slice(0, state.pageLimit),
      export: {
        ...state.export,
        exportView: {
          ...state.export.exportView,
          data: newExportedView,
        },
      },
    };
    setState(updated);
  };

  const clearFilters = () => {
    const offset = (state.currentPage - 1) * state.pageLimit;
    const filteredList = searchArrayByValue(list, state.searchWord, []);
    const newExportedView = mapFromKeysEventObject(
      filteredList,
      state.headers.droppableColumnsSelected,
      customKeys
    );
    const updated = {
      ...state,
      filteredList,
      currentList: filteredList.slice(offset, offset + state.pageLimit),
      totalPages: Math.ceil(filteredList.length / state.pageLimit),
      filterKeys: [],
      export: {
        ...state.export,
        exportView: {
          ...state.export.exportView,
          data: newExportedView,
        },
      },
    };
    setState(updated);
  };

  const onSearchWordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const offset = (state.currentPage - 1) * state.pageLimit;
    const filteredList = searchArrayByValue(
      list,
      event.target.value,
      state.filterKeys
    );
    const newExportedView = mapFromKeysEventObject(
      filteredList,
      state.headers.droppableColumnsSelected,
      customKeys
    );
    const updated = {
      ...state,
      searchWord: event.target.value,
      currentList: filteredList.slice(offset, offset + state.pageLimit),
      filteredList: filteredList,
      totalRecords: filteredList.length,
      totalPages: Math.ceil(filteredList.length / state.pageLimit),
      export: {
        ...state.export,
        exportView: {
          ...state.export.exportView,
          data: newExportedView,
        },
      },
    };
    setState(updated);
  };

  useEffect(() => {
    setState({
      ...initialState,
      filteredList: list,
      totalRecords: list.length,
      currentList: list.slice(0, pageLimit),
      possibleSearchKeys: getUniqueKeysForTable(list),
      headers: {
        droppableColumnsSelected: headerRow,
        droppableColumnsAll: getUniqueKeysForTable(list).filter(
          (item) => !headerRow.includes(item)
        ),
      },
      export: {
        exportAll: {
          data: mapFromKeysEventObject(
            list,
            getUniqueKeysForTable(list),
            customKeys
          ),
          filename: initialState.export.exportAll.filename,
          label: initialState.export.exportAll.label,
        },
        exportView: {
          data: mapFromKeysEventObject(list, headerRow, customKeys),
          filename: initialState.export.exportView.filename,
          label: initialState.export.exportView.label,
        },
      },
    });
  }, [list]); //eslint-disable-line

  //TODO: Kun painaa sortkey => pidä paginointi kunnossa!!

  return (
    <div className='flex flex-col shadow-lg bg-brand-white md:shadow-lg my-5 md:w-full min-h-5/6 max-h-11/12 overflow-y-auto overflow-x-hidden scroller'>
      <ErrorFetching message={errorMsg} />
      <SearchBar
        sortKeys={state.possibleSearchKeys}
        onDragEnd={onDragEnd}
        filterDivToggleId={filterDivToggleId}
        tableSettingsDivToggleId={tableSettingsDivId}
        exportSettingsDivToggleId={exportSettingsDivId}
        selectedColumns={state.headers.droppableColumnsSelected}
        restColumns={state.headers.droppableColumnsAll}
        pageLimit={state.pageLimit}
        activeFilters={state.filterKeys}
        toggleFilter={toggleFilter}
        clearFilters={clearFilters}
        exportSettings={state.export}
        onSearchWordChange={onSearchWordChange}
      />
      {loading ? (
        <Loading styles='my-auto' />
      ) : (
        <table className='md:bg-white rounded-lg md:table-fixed w-full'>
          <EventTableHeader
            headers={state.headers.droppableColumnsSelected}
            sort={sort}
          />
          <tbody>
            {state.currentList.map((e) => (
              <EventTableRow
                headerKeys={state.headers.droppableColumnsSelected}
                event={e}
                key={e.eventId}
                name={e.eventName}
                open={openEvent}
                id={e.eventId}
                subEvents={e.subEvents}
              />
            ))}
          </tbody>
        </table>
      )}
      <Pagination
        pageLimit={state.pageLimit}
        totalPages={state.totalPages}
        pageNeighbours={state.pageNeighbours}
        currentPage={state.currentPage}
        onPageChanged={gotoPage}
      />
    </div>
  );
};
